import { useState, type FormEvent } from 'react';
import { AssetGroupsTab, DepreciationMethodsTab } from '@/pages/platform/AssetRefsTabs';
import AiCreditsTab from '@/pages/platform/AiCreditsTab';
import Migration1cTab from './Migration1cTab';
import ProcessingWaitlistTab from './ProcessingWaitlistTab';
import PlatformBillingTab from '@/pages/platform/PlatformBillingTab';
import UomClassifierTab from '@/pages/platform/UomClassifierTab';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import DatePopover from '@/components/DatePopover';
import {
  CASH_FLOW_TYPE_LABELS,
  type CashFlowType,
  type PlatformCashFlowItem,
  type PlatformCashFlowItemCreate,
} from '@/api/platformRefs';
import {
  PLATFORM_SECTIONS,
  DEFAULT_PLATFORM_SECTION,
  isPlatformSection,
} from '@/pages/platform/platformSections';
import {
  getPlatformStatus,
  totpSetup,
  totpEnable,
  type TotpSetupResult,
} from '@/api/auth';
import {
  platformEusign,
  platformRefs,
  platformConstants,
  platformClassifiers,
  platformCalendar,
  platformIncomeTypes,
  platformDismissalGrounds,
  platformInflation,
  platformWorktimeCodes,
  platformDisabilityReasons,
  platformTaxList,
  type Currency,
  type CurrencyCreate,
  type VatRate,
  type VatRateCreate,
  type Bank,
  type BankCreate,
  type SourceConstant,
  type ConstantRange,
  type DerivedConstant,
  type AddRange,
  type ClassifierStatus,
  type CalendarYearSummary,
  type CalendarDay,
  type IncomeType,
  type IncomeTypeCreate,
  type DismissalGround,
  type DismissalGroundCreate,
  type InflationIndex,
  type WorktimeCode,
  type DisabilityReason,
  type TaxListItem,
  type PlatformTaxPurpose,
  type PlatformTaxPurposeCreate,
  type PlatformTaxationScheme,
  type PlatformTaxationSchemeCreate,
  type PlatformTaxOffice,
  type PlatformTaxOfficeCreate,
} from '@/api/platformRefs';
import TchTable from '@/shared/components/TchTable';

/**
 * Platform Admin Блок B — CRUD глобальних довідників (валюти, ставки ПДВ,
 * банки) у super-admin розділі /platform + блок керування 2FA (Google
 * Authenticator) для платформенного власника.
 *
 * Довідники глобальні (не tenant-scoped) — зміни впливають на ВСІХ клієнтів
 * (про що попереджає шапка PlatformLayout). 2FA-блок збережено повністю —
 * це окрема секція внизу сторінки.
 */

export default function PlatformSystemDataPage() {
  // Активний розділ — з query-параметра ?tab= (меню у лівому sidebar layout'а).
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = isPlatformSection(tabParam) ? tabParam : DEFAULT_PLATFORM_SECTION;
  const sectionLabel =
    PLATFORM_SECTIONS.find((s) => s.id === tab)?.label ?? 'Системні дані';

  const status = useQuery({
    queryKey: ['platform-status'],
    queryFn: getPlatformStatus,
    retry: false,
  });

  // 2FA — окрема секція, показуємо лише на першому розділі (щоб не дублювати
  // на кожному). Завжди доступна через «Валюти» (default).
  const showTwoFactor = tab === DEFAULT_PLATFORM_SECTION;

  return (
    <div>
      <div className="toolbar">
        <h2>{sectionLabel}</h2>
      </div>

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <div className="tabs__panel">
          {tab === 'currencies' && <CurrenciesTab />}
          {tab === 'vat-rates' && <VatRatesTab />}
          {tab === 'banks' && <BanksTab />}
          {tab === 'tax-constants' && <TaxConstantsTab />}
          {tab === 'dps-certs' && <DpsCertsTab />}
          {tab === 'ai-credits' && <AiCreditsTab />}
          {tab === 'billing' && <PlatformBillingTab />}
          {tab === 'classifiers' && <ClassifiersTab />}
          {tab === 'uoms' && <UomClassifierTab />}
          {tab === 'calendar' && <CalendarTab />}
          {tab === 'income-types' && <IncomeTypesTab />}
          {tab === 'dismissal-grounds' && <DismissalGroundsTab />}
          {tab === 'inflation' && <InflationTab />}
          {tab === 'worktime' && <WorktimeCodesTab />}
          {tab === 'disability' && <DisabilityReasonsTab />}
          {tab === 'tax-list' && <TaxListTab />}
          {tab === 'tax-purposes' && <TaxPurposesTab />}
          {tab === 'cash-flow-items' && <CashFlowItemsTab />}
          {tab === 'taxation-schemes' && <TaxationSchemesTab />}
          {tab === 'tax-offices' && <TaxOfficesTab />}
          {tab === 'asset-groups' && <AssetGroupsTab />}
          {tab === 'depreciation-methods' && <DepreciationMethodsTab />}
          {tab === 'migration-1c' && <Migration1cTab />}
          {tab === 'processing-waitlist' && <ProcessingWaitlistTab />}
        </div>
      </div>

      {showTwoFactor && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Двофакторна автентифікація</h3>
          {status.isLoading ? (
            <p style={{ color: '#94a3b8' }}>Завантаження…</p>
          ) : status.data?.totp_enabled ? (
            <p style={{ color: '#16a34a', fontWeight: 600 }}>✓ 2FA активна</p>
          ) : (
            <TwoFactorSetup onEnabled={() => status.refetch()} />
          )}
        </div>
      )}
    </div>
  );
}

/** Витягнути людиночитане повідомлення з помилки axios (detail з backend). */
function errMessage(e: unknown, fallback: string): string {
  const err = e as AxiosError<{ detail?: string }>;
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return fallback;
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  borderBottom: '2px solid var(--border)',
  fontSize: '0.8rem',
  color: 'var(--muted)',
  whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
};

/* =========================== ВАЛЮТИ =========================== */

function CurrenciesTab() {
  const qc = useQueryClient();
  const KEY = ['platform-currencies'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.currencies.list });

  const [editing, setEditing] = useState<Currency | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: CurrencyCreate) => platformRefs.currencies.create(p),
    onSuccess: () => {
      setAdding(false);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити валюту.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: CurrencyCreate }) =>
      platformRefs.currencies.update(id, p),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити валюту.')),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => platformRefs.currencies.remove(id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося видалити валюту.')),
  });

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Глобальний довідник валют
        </span>
        <button
          className="primary"
          onClick={() => {
            setAdding(true);
            setEditing(null);
            setError(null);
          }}
        >
          + Додати
        </button>
      </div>

      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {adding && (
        <CurrencyForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
        />
      )}

      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={thStyle}>Скорочення</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((c) =>
              editing?.id === c.id ? (
                <tr key={c.id}>
                  <td style={tdStyle} colSpan={4}>
                    <CurrencyForm
                      initial={c}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ id: c.id, p })}
                      onCancel={() => {
                        setEditing(null);
                        setError(null);
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={c.id}>
                  <td style={tdStyle}>{c.code}</td>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={tdStyle}>{c.name_short ?? '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => {
                        setEditing(c);
                        setAdding(false);
                        setError(null);
                      }}
                    >
                      Редагувати
                    </button>{' '}
                    <button
                      className="danger"
                      disabled={removeMut.isPending}
                      onClick={() => {
                        if (confirm(`Видалити валюту «${c.code}»?`)) removeMut.mutate(c.id);
                      }}
                    >
                      Видалити
                    </button>
                  </td>
                </tr>
              ),
            )}
            {(list.data ?? []).length === 0 && (
              <tr>
                <td style={{ ...tdStyle, color: '#94a3b8' }} colSpan={4}>
                  Немає валют
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CurrencyForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
}: {
  initial?: Currency;
  submitting: boolean;
  onSubmit: (p: CurrencyCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [numericCode, setNumericCode] = useState(initial?.numeric_code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [nameShort, setNameShort] = useState(initial?.name_short ?? '');

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      code: code.trim().toUpperCase(),
      numeric_code: numericCode.trim() || null,
      name: name.trim(),
      name_short: nameShort.trim() || null,
    });
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'flex-end',
        padding: '0.75rem',
        background: 'var(--bg-soft-2)',
        borderRadius: '6px',
        marginBottom: '0.75rem',
      }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input
          value={code}
          maxLength={3}
          required
          style={{ width: '90px' }}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Числовий код</label>
        <input
          value={numericCode}
          style={{ width: '110px' }}
          onChange={(e) => setNumericCode(e.target.value)}
        />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '180px' }}>
        <label>Назва*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Скорочення</label>
        <input
          value={nameShort}
          style={{ width: '120px' }}
          onChange={(e) => setNameShort(e.target.value)}
        />
      </div>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>
        Скасувати
      </button>
    </form>
  );
}

/* ========================== СТАВКИ ПДВ ========================== */

function VatRatesTab() {
  const qc = useQueryClient();
  const KEY = ['platform-vat-rates'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.vatRates.list });

  const [editing, setEditing] = useState<VatRate | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: VatRateCreate) => platformRefs.vatRates.create(p),
    onSuccess: () => {
      setAdding(false);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити ставку.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: VatRateCreate }) =>
      platformRefs.vatRates.update(id, p),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити ставку.')),
  });

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Глобальний довідник ставок ПДВ
        </span>
        <button
          className="primary"
          onClick={() => {
            setAdding(true);
            setEditing(null);
            setError(null);
          }}
        >
          + Додати
        </button>
      </div>

      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {adding && (
        <VatRateForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
        />
      )}

      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Ставка, %</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активна</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Порядок</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((v) =>
              editing?.id === v.id ? (
                <tr key={v.id}>
                  <td style={tdStyle} colSpan={6}>
                    <VatRateForm
                      initial={v}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ id: v.id, p })}
                      onCancel={() => {
                        setEditing(null);
                        setError(null);
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={v.id}>
                  <td style={tdStyle}>{v.code}</td>
                  <td style={tdStyle}>{v.name}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{v.rate_percent}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {v.is_active ? '☑' : '☐'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{v.sort_order}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => {
                        setEditing(v);
                        setAdding(false);
                        setError(null);
                      }}
                    >
                      Редагувати
                    </button>
                  </td>
                </tr>
              ),
            )}
            {(list.data ?? []).length === 0 && (
              <tr>
                <td style={{ ...tdStyle, color: '#94a3b8' }} colSpan={6}>
                  Немає ставок
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function VatRateForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
}: {
  initial?: VatRate;
  submitting: boolean;
  onSubmit: (p: VatRateCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [ratePercent, setRatePercent] = useState(
    initial ? String(initial.rate_percent) : '',
  );
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [sortOrder, setSortOrder] = useState(
    initial ? String(initial.sort_order) : '0',
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      rate_percent: Number(ratePercent) || 0,
      is_active: isActive,
      sort_order: Number(sortOrder) || 0,
    });
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'flex-end',
        padding: '0.75rem',
        background: 'var(--bg-soft-2)',
        borderRadius: '6px',
        marginBottom: '0.75rem',
      }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input
          value={code}
          required
          style={{ width: '110px' }}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '160px' }}>
        <label>Назва*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Ставка, %*</label>
        <input
          type="number"
          step="0.01"
          value={ratePercent}
          required
          style={{ width: '100px' }}
          onChange={(e) => setRatePercent(e.target.value)}
        />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Порядок</label>
        <input
          type="number"
          value={sortOrder}
          style={{ width: '90px' }}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input
          type="checkbox"
          checked={isActive}
          style={{ width: 'auto' }}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        Активна
      </label>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>
        Скасувати
      </button>
    </form>
  );
}

/* ============================= БАНКИ ============================= */

function BanksTab() {
  const qc = useQueryClient();
  const KEY = ['platform-banks'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.banks.list });

  const [editing, setEditing] = useState<Bank | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: BankCreate) => platformRefs.banks.create(p),
    onSuccess: () => {
      setAdding(false);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити банк.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ mfo, p }: { mfo: string; p: BankCreate }) =>
      platformRefs.banks.update(mfo, p),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити банк.')),
  });

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Глобальний довідник банків (МФО)
        </span>
        <button
          className="primary"
          onClick={() => {
            setAdding(true);
            setEditing(null);
            setError(null);
          }}
        >
          + Додати
        </button>
      </div>

      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

      {adding && (
        <BankForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
        />
      )}

      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>МФО</th>
              <th style={thStyle}>Назва</th>
              <th style={thStyle}>ЄДРПОУ</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активний</th>
              <th style={thStyle}>Джерело</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((b) =>
              editing?.mfo === b.mfo ? (
                <tr key={b.mfo}>
                  <td style={tdStyle} colSpan={6}>
                    <BankForm
                      initial={b}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ mfo: b.mfo, p })}
                      onCancel={() => {
                        setEditing(null);
                        setError(null);
                      }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={b.mfo}>
                  <td style={tdStyle}>{b.mfo}</td>
                  <td style={tdStyle}>{b.name}</td>
                  <td style={tdStyle}>{b.edrpou ?? '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {b.is_active ? '☑' : '☐'}
                  </td>
                  <td style={tdStyle}>{b.source}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => {
                        setEditing(b);
                        setAdding(false);
                        setError(null);
                      }}
                    >
                      Редагувати
                    </button>
                  </td>
                </tr>
              ),
            )}
            {(list.data ?? []).length === 0 && (
              <tr>
                <td style={{ ...tdStyle, color: '#94a3b8' }} colSpan={6}>
                  Немає банків
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BankForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
}: {
  initial?: Bank;
  submitting: boolean;
  onSubmit: (p: BankCreate) => void;
  onCancel: () => void;
}) {
  // МФО — первинний ключ. При редагуванні не дозволяємо змінювати (backend
  // PATCH приймає mfo у body, але для UX лочимо поле — зміна ключа = новий банк).
  const isEdit = Boolean(initial);
  const [mfo, setMfo] = useState(initial?.mfo ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [fullName, setFullName] = useState(initial?.full_name ?? '');
  const [edrpou, setEdrpou] = useState(initial?.edrpou ?? '');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      mfo: mfo.trim(),
      name: name.trim(),
      full_name: fullName.trim() || null,
      edrpou: edrpou.trim() || null,
      is_active: isActive,
    });
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'flex-end',
        padding: '0.75rem',
        background: 'var(--bg-soft-2)',
        borderRadius: '6px',
        marginBottom: '0.75rem',
      }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>МФО*</label>
        <input
          value={mfo}
          maxLength={6}
          required
          disabled={isEdit}
          style={{ width: '100px' }}
          onChange={(e) => setMfo(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '160px' }}>
        <label>Назва*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '160px' }}>
        <label>Повна назва</label>
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>ЄДРПОУ</label>
        <input
          value={edrpou}
          style={{ width: '120px' }}
          onChange={(e) => setEdrpou(e.target.value)}
        />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input
          type="checkbox"
          checked={isActive}
          style={{ width: 'auto' }}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        Активний
      </label>
      <button type="submit" className="primary" disabled={submitting || !mfo.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>
        Скасувати
      </button>
    </form>
  );
}

/* ===================== ПОДАТКОВІ КОНСТАНТИ (Блок C) ===================== */

function TaxConstantsTab() {
  const [sub, setSub] = useState<'source' | 'derived' | 'psp'>('source');
  return (
    <div>
      <div
        style={{
          padding: '0.6rem 0.75rem',
          background: 'var(--badge-amber-bg)',
          border: '1px solid var(--badge-amber-bd)',
          borderRadius: '6px',
          marginBottom: '0.9rem',
          fontSize: '0.85rem',
          color: 'var(--badge-amber-fg)',
        }}
      >
        ⚠ Ці значення — першоджерело розрахунків зарплати, ЄСВ, відпусток,
        лікарняних і єдиного податку для ВСІХ клієнтів. Зміни застосовуються
        протягом 5 хвилин. Додавайте новий діапазон з дати набуття чинності
        закону — старі значення зберігаються (історія).
      </div>
      <div className="tabs__nav" style={{ margin: '0 0 0.9rem' }}>
        <button
          className={sub === 'source' ? 'active' : ''}
          onClick={() => setSub('source')}
        >
          Базові (редаговані)
        </button>
        <button
          className={sub === 'derived' ? 'active' : ''}
          onClick={() => setSub('derived')}
        >
          Похідні (прев'ю)
        </button>
        <button
          className={sub === 'psp' ? 'active' : ''}
          onClick={() => setSub('psp')}
        >
          Пільги ПСП
        </button>
      </div>
      {sub === 'source' && <SourceConstantsTable />}
      {sub === 'derived' && <DerivedConstantsTable />}
      {sub === 'psp' && <PspCategoriesTable />}
    </div>
  );
}

function PspCategoriesTable() {
  const list = useQuery({
    queryKey: ['platform-psp-categories'],
    queryFn: platformConstants.pspCategories,
  });
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Категорії податкової соціальної пільги (ПКУ ст.169). Перелік і множники
        фіксовані законом — read-only. Сума ПСП = базова пільга × множник
        (× кількість дітей для відповідних категорій).
      </p>
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Категорія</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Множник</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>× дітей</th>
              <th style={thStyle}>Підстава</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((c) => (
              <tr key={c.code}>
                <td style={tdStyle}>
                  <div>{c.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--muted)', fontFamily: 'monospace' }}>
                    {c.code}
                  </div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                  {c.multiplier}×
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {c.requires_children ? '☑' : '—'}
                </td>
                <td style={{ ...tdStyle, fontSize: '0.82rem' }}>
                  {c.legal_reference}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SourceConstantsTable() {
  const KEY = ['platform-tax-constants'];
  const list = useQuery({ queryKey: KEY, queryFn: platformConstants.list });
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        10 базових констант. Натисніть «Історія» щоб переглянути діапазони та
        додати новий.
      </p>
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Опис</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Поточне</th>
              <th style={thStyle}>Од.</th>
              <th style={thStyle}>З дати</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((c) => (
              <SourceConstantRow
                key={c.code}
                c={c}
                expanded={expanded === c.code}
                onToggle={() =>
                  setExpanded(expanded === c.code ? null : c.code)
                }
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SourceConstantRow({
  c,
  expanded,
  onToggle,
}: {
  c: SourceConstant;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr>
        <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {c.code}
        </td>
        <td style={tdStyle}>{c.description}</td>
        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
          {c.value}
        </td>
        <td style={tdStyle}>{c.unit}</td>
        <td style={tdStyle}>{c.effective_from}</td>
        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button onClick={onToggle}>{expanded ? 'Згорнути' : 'Історія'}</button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td style={{ ...tdStyle, background: 'var(--bg-soft-2)' }} colSpan={6}>
            <ConstantHistoryPanel code={c.code} unit={c.unit} />
          </td>
        </tr>
      )}
    </>
  );
}

function ConstantHistoryPanel({ code, unit }: { code: string; unit: string }) {
  const qc = useQueryClient();
  const HKEY = ['platform-tax-constant-history', code];
  const history = useQuery({
    queryKey: HKEY,
    queryFn: () => platformConstants.history(code),
  });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addMut = useMutation({
    mutationFn: (p: AddRange) => platformConstants.addRange(code, p),
    onSuccess: () => {
      setAdding(false);
      setError(null);
      qc.invalidateQueries({ queryKey: HKEY });
      qc.invalidateQueries({ queryKey: ['platform-tax-constants'] });
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося додати діапазон.')),
  });

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: '0.6rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
          Історія діапазонів (append-only)
        </span>
        <button className="primary" onClick={() => { setAdding(true); setError(null); }}>
          + Новий діапазон
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.6rem' }}>{error}</div>}
      {adding && (
        <AddRangeForm
          unit={unit}
          submitting={addMut.isPending}
          onSubmit={(p) => addMut.mutate(p)}
          onCancel={() => { setAdding(false); setError(null); }}
        />
      )}
      {history.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>З дати</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Значення</th>
              <th style={thStyle}>Од.</th>
              <th style={thStyle}>Підстава</th>
            </tr>
          </thead>
          <tbody>
            {(history.data ?? []).map((r: ConstantRange) => (
              <tr key={r.id}>
                <td style={tdStyle}>{r.effective_from}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{r.value}</td>
                <td style={tdStyle}>{r.unit}</td>
                <td style={{ ...tdStyle, fontSize: '0.82rem' }}>{r.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AddRangeForm({
  unit,
  submitting,
  onSubmit,
  onCancel,
}: {
  unit: string;
  submitting: boolean;
  onSubmit: (p: AddRange) => void;
  onCancel: () => void;
}) {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [value, setValue] = useState('');
  const [source, setSource] = useState('');
  const [description, setDescription] = useState('');

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      effective_from: effectiveFrom,
      value: Number(value) || 0,
      unit,
      description: description.trim(),
      source: source.trim(),
    });
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'flex-end',
        padding: '0.75rem',
        background: 'var(--bg-panel)',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        marginBottom: '0.75rem',
      }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>Чинна з*</label>
        <DatePopover
          value={effectiveFrom}
          onChange={(v) => setEffectiveFrom(v)}
        />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Значення* ({unit})</label>
        <input
          type="number"
          step="0.0001"
          value={value}
          required
          style={{ width: '130px' }}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '180px' }}>
        <label>Опис*</label>
        <input
          value={description}
          required
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '180px' }}>
        <label>Підстава (закон)*</label>
        <input
          value={source}
          required
          placeholder="Закон №… про Держбюджет на…"
          onChange={(e) => setSource(e.target.value)}
        />
      </div>
      <button
        type="submit"
        className="primary"
        disabled={submitting || !effectiveFrom || !value || !description.trim() || !source.trim()}
      >
        {submitting ? 'Збереження…' : 'Додати'}
      </button>
      <button type="button" onClick={onCancel}>
        Скасувати
      </button>
    </form>
  );
}

function DerivedConstantsTable() {
  const list = useQuery({
    queryKey: ['platform-tax-derived'],
    queryFn: platformConstants.derived,
  });
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Похідні значення обчислюються з базових констант за формулами у коді —
        редагувати їх не можна. Показано для прозорості «що з цього випливає».
      </p>
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Опис</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Значення</th>
              <th style={thStyle}>Од.</th>
              <th style={thStyle}>Формула</th>
              <th style={thStyle}>Підстава</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((d: DerivedConstant) => (
              <tr key={d.code}>
                <td style={tdStyle}>
                  {d.description}
                  {d.category === 'SNAPSHOT_YEAR_START' && (
                    <span
                      title="Фіксується на 1 січня року"
                      style={{ marginLeft: '0.4rem', color: '#0ea5e9', fontSize: '0.75rem' }}
                    >
                      ❄ на 01.01
                    </span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                  {d.value}
                </td>
                <td style={tdStyle}>{d.unit}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  {d.formula}
                </td>
                <td style={{ ...tdStyle, fontSize: '0.82rem' }}>{d.legal_reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ===================== КЛАСИФІКАТОРИ (моніторинг) ===================== */

function ClassifiersTab() {
  const list = useQuery({
    queryKey: ['platform-classifiers'],
    queryFn: platformClassifiers.list,
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Стан великих державних класифікаторів. Оновлення поки виконується
        технічно (міграцією) — ця сторінка дозволяє стежити за актуальністю.
        КВЕД (ДК 009) поки не ведеться окремим довідником.
      </p>
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Класифікатор</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Кодів</th>
              <th style={thStyle}>Оновлено</th>
              <th style={thStyle}>Джерело в базі</th>
              <th style={thStyle}>Актуальна версія</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Статус</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((c: ClassifierStatus) => (
              <tr key={c.key}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600 }}>{c.title}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                    {c.description}
                  </div>
                  <a
                    href={c.source_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '0.75rem' }}
                  >
                    першоджерело ↗
                  </a>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {c.code_count.toLocaleString('uk-UA')}
                </td>
                <td style={tdStyle}>{c.last_updated ?? '—'}</td>
                <td style={{ ...tdStyle, fontSize: '0.8rem' }}>
                  {c.seed_source ?? '—'}
                </td>
                <td style={{ ...tdStyle, fontSize: '0.82rem' }}>
                  {c.current_version}
                  <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>
                    {c.change_frequency}
                  </div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <ClassifierBadge status={c.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ClassifierBadge({ status }: { status: 'OK' | 'CHECK' | 'EMPTY' }) {
  const map = {
    OK: { label: '✓ Актуально', bg: '#dcfce7', fg: '#166534' },
    CHECK: { label: '⚠ Перевірити', bg: '#fef3c7', fg: '#92400e' },
    EMPTY: { label: '✕ Порожньо', bg: '#fee2e2', fg: '#991b1b' },
  } as const;
  const s = map[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.2rem 0.55rem',
        borderRadius: '999px',
        fontSize: '0.78rem',
        fontWeight: 600,
        background: s.bg,
        color: s.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

/* ===================== ВИРОБНИЧИЙ КАЛЕНДАР ===================== */

function CalendarTab() {
  const summary = useQuery({
    queryKey: ['platform-calendar-summary'],
    queryFn: platformCalendar.summary,
  });

  const years = Array.from(
    new Set((summary.data ?? []).map((s) => s.year)),
  ).sort((a, b) => b - a);
  const [year, setYear] = useState<number | null>(null);
  const activeYear = year ?? years[0] ?? null;
  // Сценарій: воєнний за замовч. (розрахунки беруть wartime з 2022).
  const [isWartime, setIsWartime] = useState(true);

  return (
    <div>
      <div
        style={{
          padding: '0.6rem 0.75rem',
          background: 'var(--badge-amber-bg)',
          border: '1px solid var(--badge-amber-bd)',
          borderRadius: '6px',
          marginBottom: '0.9rem',
          fontSize: '0.85rem',
          color: 'var(--badge-amber-fg)',
        }}
      >
        ⚠ Глобальний календар для розрахунків відпусток/лікарняних/зарплати
        всіх клієнтів. <strong>Воєнний стан (Закон 2210-IX, з 2022):</strong>{' '}
        частина свят стає робочими днями. Перемикач нижче показує обидва
        сценарії — розрахунки за замовчуванням беруть воєнний.
      </div>

      {/* Зведення по роках і сценаріях */}
      {summary.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
          <thead>
            <tr>
              <th style={thStyle}>Рік</th>
              <th style={thStyle}>Сценарій</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Свят-вихідних</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Свят стали робочими</th>
            </tr>
          </thead>
          <tbody>
            {(summary.data ?? []).map((s: CalendarYearSummary) => (
              <tr key={`${s.year}-${s.scenario}`}>
                <td style={tdStyle}>{s.year}</td>
                <td style={tdStyle}>
                  {s.is_wartime ? '🪖 Воєнний' : '🕊 Мирний'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{s.holidays}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {s.workday_overrides || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Перегляд днів обраного року + сценарію */}
      <div className="toolbar" style={{ gap: '0.75rem', marginBottom: '0.75rem' }}>
        <label>
          Рік:{' '}
          <select
            value={activeYear ?? ''}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <div className="tabs__nav" style={{ margin: 0 }}>
          <button
            className={!isWartime ? 'active' : ''}
            onClick={() => setIsWartime(false)}
          >
            🕊 Мирний
          </button>
          <button
            className={isWartime ? 'active' : ''}
            onClick={() => setIsWartime(true)}
          >
            🪖 Воєнний
          </button>
        </div>
      </div>

      {activeYear !== null && (
        <CalendarDaysList year={activeYear} isWartime={isWartime} />
      )}
    </div>
  );
}

function CalendarDaysList({
  year,
  isWartime,
}: {
  year: number;
  isWartime: boolean;
}) {
  const qc = useQueryClient();
  const KEY = ['platform-calendar-days', year, isWartime];
  const days = useQuery({
    queryKey: KEY,
    queryFn: () => platformCalendar.days(year, isWartime),
  });
  const [editing, setEditing] = useState<string | null>(null);

  const patchMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: { day_type?: string; holiday_name?: string | null } }) =>
      platformCalendar.patchDay(id, p),
    onSuccess: () => {
      setEditing(null);
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['platform-calendar-summary'] });
    },
  });

  if (days.isLoading) return <p style={{ color: '#94a3b8' }}>Завантаження…</p>;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={thStyle}>Дата</th>
          <th style={thStyle}>Тип</th>
          <th style={thStyle}>Назва свята</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
        </tr>
      </thead>
      <tbody>
        {(days.data ?? []).map((d: CalendarDay) => (
          <tr key={d.id}>
            <td style={tdStyle}>{d.day_date}</td>
            <td style={tdStyle}>
              {d.day_type === 'HOLIDAY' ? '🎉 Свято' : '💼 Робочий (відмінено)'}
            </td>
            <td style={tdStyle}>
              {editing === d.id ? (
                <input
                  defaultValue={d.holiday_name ?? ''}
                  id={`hn-${d.id}`}
                  style={{ width: '100%' }}
                />
              ) : (
                d.holiday_name ?? '—'
              )}
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
              {editing === d.id ? (
                <>
                  <button
                    className="primary"
                    disabled={patchMut.isPending}
                    onClick={() => {
                      const el = document.getElementById(
                        `hn-${d.id}`,
                      ) as HTMLInputElement | null;
                      patchMut.mutate({
                        id: d.id,
                        p: { holiday_name: el?.value.trim() || null },
                      });
                    }}
                  >
                    Зберегти
                  </button>{' '}
                  <button onClick={() => setEditing(null)}>Скасувати</button>
                </>
              ) : (
                <button onClick={() => setEditing(d.id)}>Редагувати</button>
              )}
            </td>
          </tr>
        ))}
        {(days.data ?? []).length === 0 && (
          <tr>
            <td style={{ ...tdStyle, color: '#94a3b8' }} colSpan={4}>
              Немає особливих днів
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/* ===================== ОЗНАКИ ДОХОДІВ НДФЛ (4ДФ) ===================== */

function IncomeTypesTab() {
  const qc = useQueryClient();
  const KEY = ['platform-income-types'];
  const list = useQuery({ queryKey: KEY, queryFn: platformIncomeTypes.list });
  const [editing, setEditing] = useState<IncomeType | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: IncomeTypeCreate) => platformIncomeTypes.create(p),
    onSuccess: () => { setAdding(false); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: IncomeTypeCreate }) =>
      platformIncomeTypes.update(id, p),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити.')),
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Довідник ознак доходів фізосіб (Наказ Мінфіну №4) для Додатку 4ДФ.
        Зарплата → завжди 101, ЦПУ → 102 (фіксовано законом). Інші ознаки —
        для ручного вибору. Можна додати нові коди при оновленні довідника ДПС.
      </p>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Глобальний довідник ознак доходів
        </span>
        <button className="primary" onClick={() => { setAdding(true); setEditing(null); setError(null); }}>
          + Додати
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {adding && (
        <IncomeTypeForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => { setAdding(false); setError(null); }}
        />
      )}
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Зарплатна</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активна</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((t) =>
              editing?.id === t.id ? (
                <tr key={t.id}>
                  <td style={tdStyle} colSpan={5}>
                    <IncomeTypeForm
                      initial={t}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ id: t.id, p })}
                      onCancel={() => { setEditing(null); setError(null); }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={t.id}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{t.code}</td>
                  <td style={tdStyle}>{t.name}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{t.is_payroll ? '☑' : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{t.is_active ? '☑' : '☐'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button onClick={() => { setEditing(t); setAdding(false); setError(null); }}>
                      Редагувати
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function IncomeTypeForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
}: {
  initial?: IncomeType;
  submitting: boolean;
  onSubmit: (p: IncomeTypeCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [isPayroll, setIsPayroll] = useState(initial?.is_payroll ?? false);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [sortOrder, setSortOrder] = useState(initial ? String(initial.sort_order) : '100');

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      is_payroll: isPayroll,
      is_active: isActive,
      sort_order: Number(sortOrder) || 100,
    });
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
        padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: '6px', marginBottom: '0.75rem',
      }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input value={code} maxLength={10} required style={{ width: '90px' }}
          onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '220px' }}>
        <label>Назва*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Порядок</label>
        <input type="number" value={sortOrder} style={{ width: '90px' }}
          onChange={(e) => setSortOrder(e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={isPayroll} style={{ width: 'auto' }}
          onChange={(e) => setIsPayroll(e.target.checked)} />
        Зарплатна
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={isActive} style={{ width: 'auto' }}
          onChange={(e) => setIsActive(e.target.checked)} />
        Активна
      </label>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>Скасувати</button>
    </form>
  );
}

/* ===================== ПІДСТАВИ ЗВІЛЬНЕННЯ (КЗпП) ===================== */

function DismissalGroundsTab() {
  const qc = useQueryClient();
  const KEY = ['platform-dismissal-grounds'];
  const list = useQuery({ queryKey: KEY, queryFn: platformDismissalGrounds.list });
  const [editing, setEditing] = useState<DismissalGround | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: DismissalGroundCreate) => platformDismissalGrounds.create(p),
    onSuccess: () => { setAdding(false); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: DismissalGroundCreate }) =>
      platformDismissalGrounds.update(id, p),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити.')),
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Довідник підстав звільнення (статті КЗпП України). Використовується у
        наказах про звільнення. Можна додати нові статті за потреби.
      </p>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Глобальний довідник підстав звільнення
        </span>
        <button className="primary" onClick={() => { setAdding(true); setEditing(null); setError(null); }}>
          + Додати
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {adding && (
        <DismissalGroundForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => { setAdding(false); setError(null); }}
        />
      )}
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Стаття КЗпП</th>
              <th style={thStyle}>Підстава</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Ініціатива прац.</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активна</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((g) =>
              editing?.id === g.id ? (
                <tr key={g.id}>
                  <td style={tdStyle} colSpan={5}>
                    <DismissalGroundForm
                      initial={g}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ id: g.id, p })}
                      onCancel={() => { setEditing(null); setError(null); }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={g.id}>
                  <td style={{ ...tdStyle, fontWeight: 600, whiteSpace: 'nowrap' }}>{g.article}</td>
                  <td style={tdStyle}>{g.name}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{g.is_by_employee ? '☑' : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{g.is_active ? '☑' : '☐'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button onClick={() => { setEditing(g); setAdding(false); setError(null); }}>
                      Редагувати
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DismissalGroundForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
}: {
  initial?: DismissalGround;
  submitting: boolean;
  onSubmit: (p: DismissalGroundCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [article, setArticle] = useState(initial?.article ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [byEmployee, setByEmployee] = useState(initial?.is_by_employee ?? false);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [sortOrder, setSortOrder] = useState(initial ? String(initial.sort_order) : '100');

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      code: code.trim(),
      article: article.trim(),
      name: name.trim(),
      is_by_employee: byEmployee,
      is_active: isActive,
      sort_order: Number(sortOrder) || 100,
    });
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
        padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: '6px', marginBottom: '0.75rem',
      }}
    >
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input value={code} maxLength={30} required style={{ width: '140px' }}
          onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Стаття КЗпП*</label>
        <input value={article} maxLength={50} required style={{ width: '150px' }}
          onChange={(e) => setArticle(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '220px' }}>
        <label>Підстава*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Порядок</label>
        <input type="number" value={sortOrder} style={{ width: '80px' }}
          onChange={(e) => setSortOrder(e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={byEmployee} style={{ width: 'auto' }}
          onChange={(e) => setByEmployee(e.target.checked)} />
        Ініціатива прац.
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={isActive} style={{ width: 'auto' }}
          onChange={(e) => setIsActive(e.target.checked)} />
        Активна
      </label>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !article.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>Скасувати</button>
    </form>
  );
}

/* ===================== ІНДЕКС ІНФЛЯЦІЇ (ІСЦ) ===================== */

const MONTH_NAMES = [
  '', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
];

function InflationTab() {
  const qc = useQueryClient();
  const KEY = ['platform-inflation'];
  const list = useQuery({ queryKey: KEY, queryFn: platformInflation.list });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('1');
  const [value, setValue] = useState('');

  const createMut = useMutation({
    mutationFn: () =>
      platformInflation.create({
        year: Number(year),
        month: Number(month),
        value: Number(value),
        source: 'Держстат',
      }),
    onSuccess: () => {
      setAdding(false); setError(null); setYear(''); setValue('');
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e) => setError(errMessage(e, 'Не вдалося додати.')),
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Помісячний індекс інфляції (ІСЦ Держстату) — для індексації зарплати та
        довідково. Значення у % до попереднього місяця (101.20 = +1.2%). Додавайте
        щомісяця після публікації Держстату.
      </p>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Глобальний довідник ІСЦ
        </span>
        <button className="primary" onClick={() => { setAdding(true); setError(null); }}>
          + Додати місяць
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {adding && (
        <form
          onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}
          style={{
            display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
            padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: '6px', marginBottom: '0.75rem',
          }}
        >
          <div className="field" style={{ margin: 0 }}>
            <label>Рік*</label>
            <input type="number" value={year} required style={{ width: '90px' }}
              onChange={(e) => setYear(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Місяць*</label>
            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              {MONTH_NAMES.slice(1).map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>ІСЦ, %*</label>
            <input type="number" step="0.01" value={value} required style={{ width: '110px' }}
              placeholder="101.20" onChange={(e) => setValue(e.target.value)} />
          </div>
          <button type="submit" className="primary" disabled={createMut.isPending || !year || !value}>
            {createMut.isPending ? 'Збереження…' : 'Додати'}
          </button>
          <button type="button" onClick={() => { setAdding(false); setError(null); }}>Скасувати</button>
        </form>
      )}
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Період</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>ІСЦ, %</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Інфляція</th>
              <th style={thStyle}>Джерело</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((r: InflationIndex) => {
              const infl = (Number(r.value) - 100).toFixed(2);
              const positive = Number(infl) >= 0;
              return (
                <tr key={r.id}>
                  <td style={tdStyle}>{MONTH_NAMES[r.month]} {r.year}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{r.value}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: positive ? '#dc2626' : '#16a34a' }}>
                    {positive ? '+' : ''}{infl}%
                  </td>
                  <td style={{ ...tdStyle, fontSize: '0.82rem' }}>{r.source}</td>
                </tr>
              );
            })}
            {(list.data ?? []).length === 0 && (
              <tr>
                <td style={{ ...tdStyle, color: '#94a3b8' }} colSpan={4}>
                  Немає даних
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============= ДОВІДНИКИ З УТП (бух/податковий облік) ============= */

function WorktimeCodesTab() {
  const list = useQuery({
    queryKey: ['platform-worktime-codes'],
    queryFn: platformWorktimeCodes.list,
  });
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Класифікатор використання робочого часу (типова форма №П-5, Наказ
        Держкомстату №489). Буквені/цифрові коди табелювання.
      </p>
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Літерний код</th>
              <th style={thStyle}>Цифровий</th>
              <th style={thStyle}>Назва</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Робочий час</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((c: WorktimeCode) => (
              <tr key={c.id}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{c.letter_code}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{c.digit_code}</td>
                <td style={tdStyle}>{c.name}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{c.is_working_time ? '☑' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DisabilityReasonsTab() {
  const list = useQuery({
    queryKey: ['platform-disability-reasons'],
    queryFn: platformDisabilityReasons.list,
  });
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Причини непрацездатності (коди е-лікарняного). «Днів за рахунок
        роботодавця» — перші N днів оплачує роботодавець (загальне захворювання — 5).
      </p>
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Причина</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Днів за рах. роботодавця</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((r: DisabilityReason) => (
              <tr key={r.id}>
                <td style={{ ...tdStyle, fontWeight: 600, fontFamily: 'monospace' }}>{r.code}</td>
                <td style={tdStyle}>{r.name}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {r.days_by_employer === null ? '—' : r.days_by_employer}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TaxListTab() {
  const list = useQuery({
    queryKey: ['platform-tax-list'],
    queryFn: platformTaxList.list,
  });
  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Перелік податків та зборів (Податковий кодекс України).
      </p>
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активний</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((t: TaxListItem) => (
              <tr key={t.id}>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{t.code}</td>
                <td style={tdStyle}>{t.name}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{t.is_active ? '☑' : '☐'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============================== 2FA ============================== */

/**
 * Майстер увімкнення 2FA:
 * 1. Кнопка «Налаштувати 2FA» → totpSetup() → секрет + otpauth URI.
 * 2. Користувач додає у Google Authenticator (ручний ввід секрету або сканує
 *    otpauth-посилання) → вводить перший код → totpEnable(code).
 */
function TwoFactorSetup({ onEnabled }: { onEnabled: () => void }) {
  const [setup, setSetup] = useState<TotpSetupResult | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSetup() {
    setError(null);
    setLoading(true);
    try {
      const res = await totpSetup();
      setSetup(res);
    } catch {
      setError('Не вдалося згенерувати секрет. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  }

  async function handleEnable(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnabling(true);
    try {
      await totpEnable(code.trim());
      setEnabled(true);
      onEnabled();
    } catch {
      setError('Невірний код. Перевірте Google Authenticator і спробуйте ще раз.');
    } finally {
      setEnabling(false);
    }
  }

  if (enabled) {
    return <p style={{ color: '#16a34a', fontWeight: 600 }}>2FA увімкнено ✓</p>;
  }

  if (!setup) {
    return (
      <div>
        <p style={{ color: 'var(--text-secondary)' }}>
          Захистіть платформенний доступ другим фактором через Google Authenticator.
        </p>
        <button className="primary" onClick={handleSetup} disabled={loading}>
          {loading ? 'Генеруємо…' : 'Налаштувати 2FA'}
        </button>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)' }}>
        Додайте акаунт у Google Authenticator вручну (введіть секрет нижче) або
        перейдіть за otpauth-посиланням з пристрою, де встановлено застосунок:
      </p>
      <div className="field">
        <label>Секрет (key)</label>
        <code
          style={{
            display: 'block',
            padding: '0.6rem 0.75rem',
            background: 'var(--bg-soft)',
            borderRadius: '6px',
            fontSize: '1rem',
            letterSpacing: '0.05em',
            wordBreak: 'break-all',
          }}
        >
          {setup.secret}
        </code>
      </div>
      <div className="field">
        <label>otpauth-посилання</label>
        <a href={setup.provisioning_uri} style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
          {setup.provisioning_uri}
        </a>
      </div>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        У Google Authenticator: «+» → «Ввести ключ налаштування» → вставте секрет
        вище (тип — «На основі часу»).
      </p>

      <form onSubmit={handleEnable} style={{ marginTop: '1rem' }}>
        <div className="field">
          <label>Перший код із застосунку</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            required
          />
        </div>
        <button
          type="submit"
          className="primary"
          disabled={enabling || code.trim().length < 6}
        >
          {enabling ? 'Перевірка…' : 'Увімкнути 2FA'}
        </button>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}

/* ============ ПОДАТКОВІ ДОВІДНИКИ (перенесені з тенантських прав 2026-07-28) ============ */

function TaxPurposesTab() {
  const qc = useQueryClient();
  const KEY = ['platform-tax-purposes'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.taxPurposes.list });
  const [editing, setEditing] = useState<PlatformTaxPurpose | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: PlatformTaxPurposeCreate) => platformRefs.taxPurposes.create(p),
    onSuccess: () => { setAdding(false); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: PlatformTaxPurposeCreate }) =>
      platformRefs.taxPurposes.update(id, p),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити.')),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => platformRefs.taxPurposes.remove(id),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося видалити.')),
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Глобальний довідник податкових призначень активів/витрат (ПДВ-облік).
        Seed-записи (Internal) зашиті у логіку — їх не можна видаляти.
        Клієнти читають цей перелік у селектах, редагує лише платформа.
      </p>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Впливає на всіх клієнтів
        </span>
        <button className="primary" onClick={() => { setAdding(true); setEditing(null); setError(null); }}>
          + Додати
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {adding && (
        <TaxPurposeForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => { setAdding(false); setError(null); }}
        />
      )}
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={thStyle}>Джерело</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активне</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((t) =>
              editing?.id === t.id ? (
                <tr key={t.id}>
                  <td style={tdStyle} colSpan={5}>
                    <TaxPurposeForm
                      initial={t}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ id: t.id, p })}
                      onCancel={() => { setEditing(null); setError(null); }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={t.id}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{t.code}</td>
                  <td style={tdStyle}>{t.name_uk}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{t.seed_source ?? ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{t.is_active ? '☑' : '☐'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => { setEditing(t); setAdding(false); setError(null); }}>
                      Редагувати
                    </button>{' '}
                    {!(t.seed_source ?? '').startsWith('Internal') && (
                      <button onClick={() => removeMut.mutate(t.id)}>Видалити</button>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TaxPurposeForm({
  initial, submitting, onSubmit, onCancel,
}: {
  initial?: PlatformTaxPurpose;
  submitting: boolean;
  onSubmit: (p: PlatformTaxPurposeCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name_uk ?? '');
  const [descr, setDescr] = useState(initial?.description ?? '');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      code: code.trim(), name_uk: name.trim(),
      description: descr.trim() || null, is_active: isActive,
    });
  }

  return (
    <form onSubmit={submit} style={{
      display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
      padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: '6px', marginBottom: '0.75rem',
    }}>
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input value={code} maxLength={30} required style={{ width: '160px' }}
          onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '220px' }}>
        <label>Назва*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '180px' }}>
        <label>Опис</label>
        <input value={descr} onChange={(e) => setDescr(e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={isActive} style={{ width: 'auto' }}
          onChange={(e) => setIsActive(e.target.checked)} />
        Активне
      </label>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>Скасувати</button>
    </form>
  );
}

// ч.727: статті руху грошових коштів (ДДС) — системний довідник платформи
// (рішення власника: спільний для всіх організацій → на платформі).
// Клієнти читають у селектах банк/каса + можуть додати власні статті.
function CashFlowItemsTab() {
  const qc = useQueryClient();
  const KEY = ['platform-cash-flow-items'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.cashFlowItems.list });
  const [editing, setEditing] = useState<PlatformCashFlowItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: PlatformCashFlowItemCreate) => platformRefs.cashFlowItems.create(p),
    onSuccess: () => { setAdding(false); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: PlatformCashFlowItemCreate }) =>
      platformRefs.cashFlowItems.update(id, p),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити.')),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => platformRefs.cashFlowItems.remove(id),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося видалити.')),
  });
  const groups: CashFlowType[] = ['OPERATING', 'INVESTING', 'FINANCING'];

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Статті руху грошових коштів (звіт про рух грошових коштів, П(С)БО 4):
        операційна / інвестиційна / фінансова діяльність. Системний перелік
        бачать усі клієнти у банківських і касових документах; клієнт може
        додати власні статті у своєму кабінеті. Видалення — мʼяке (позначка),
        бо на статтю можуть посилатись документи клієнтів.
      </p>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Впливає на всіх клієнтів
        </span>
        <button className="primary" onClick={() => { setAdding(true); setEditing(null); setError(null); }}>
          + Додати
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {adding && (
        <CashFlowItemForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => { setAdding(false); setError(null); }}
        />
      )}
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={thStyle}>Вид діяльності</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const items = (list.data ?? []).filter((t) => t.flow_type === g);
              if (!items.length) return null;
              return [
                <tr key={`g-${g}`}>
                  <td style={{ ...tdStyle, fontWeight: 700, background: 'var(--bg-soft-2)' }} colSpan={4}>
                    {CASH_FLOW_TYPE_LABELS[g]} діяльність ({items.length})
                  </td>
                </tr>,
                ...items.map((t) =>
                  editing?.id === t.id ? (
                    <tr key={t.id}>
                      <td style={tdStyle} colSpan={4}>
                        <CashFlowItemForm
                          initial={t}
                          submitting={updateMut.isPending}
                          onSubmit={(p) => updateMut.mutate({ id: t.id, p })}
                          onCancel={() => { setEditing(null); setError(null); }}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={t.id}>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{t.code}</td>
                      <td style={tdStyle}>{t.name}</td>
                      <td style={{ ...tdStyle, color: 'var(--muted)' }}>{CASH_FLOW_TYPE_LABELS[t.flow_type]}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button onClick={() => { setEditing(t); setAdding(false); setError(null); }}>
                          Редагувати
                        </button>{' '}
                        <button onClick={() => {
                          if (window.confirm(`Позначити статтю «${t.name}» видаленою?`)) removeMut.mutate(t.id);
                        }}>Видалити</button>
                      </td>
                    </tr>
                  ),
                ),
              ];
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CashFlowItemForm({
  initial, submitting, onSubmit, onCancel,
}: {
  initial?: PlatformCashFlowItem;
  submitting: boolean;
  onSubmit: (p: PlatformCashFlowItemCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [flowType, setFlowType] = useState<CashFlowType>(initial?.flow_type ?? 'OPERATING');

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ code: code.trim().toUpperCase(), name: name.trim(), flow_type: flowType, parent_id: null });
  }

  return (
    <form onSubmit={submit} style={{
      display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
      padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: '6px', marginBottom: '0.75rem',
    }}>
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input value={code} maxLength={20} required style={{ width: '160px' }}
          onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '260px' }}>
        <label>Назва*</label>
        <input value={name} required maxLength={255} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Вид діяльності*</label>
        <select value={flowType} onChange={(e) => setFlowType(e.target.value as CashFlowType)}>
          {(['OPERATING', 'INVESTING', 'FINANCING'] as CashFlowType[]).map((g) => (
            <option key={g} value={g}>{CASH_FLOW_TYPE_LABELS[g]}</option>
          ))}
        </select>
      </div>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>Скасувати</button>
    </form>
  );
}

function TaxationSchemesTab() {
  const qc = useQueryClient();
  const KEY = ['platform-taxation-schemes'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.taxationSchemes.list });
  const [editing, setEditing] = useState<PlatformTaxationScheme | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: PlatformTaxationSchemeCreate) => platformRefs.taxationSchemes.create(p),
    onSuccess: () => { setAdding(false); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: PlatformTaxationSchemeCreate }) =>
      platformRefs.taxationSchemes.update(id, p),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити.')),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => platformRefs.taxationSchemes.remove(id),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося видалити.')),
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Глобальний довідник схем оподаткування (загальна/спрощена/ФОП).
        Seed-схеми зашиті у логіку карток організацій — їх не можна видаляти.
      </p>
      <div className="toolbar" style={{ marginBottom: '0.75rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          Впливає на всіх клієнтів
        </span>
        <button className="primary" onClick={() => { setAdding(true); setEditing(null); setError(null); }}>
          + Додати
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {adding && (
        <TaxationSchemeForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => { setAdding(false); setError(null); }}
        />
      )}
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>ПДВ</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Податок на прибуток, %</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Єдиний, %</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активна</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((t) =>
              editing?.id === t.id ? (
                <tr key={t.id}>
                  <td style={tdStyle} colSpan={7}>
                    <TaxationSchemeForm
                      initial={t}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ id: t.id, p })}
                      onCancel={() => { setEditing(null); setError(null); }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={t.id}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{t.code}</td>
                  <td style={tdStyle}>{t.name_uk}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{t.has_vat ? '☑' : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{t.profit_tax_rate ?? ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{t.single_tax_rate ?? ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{t.is_active ? '☑' : '☐'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => { setEditing(t); setAdding(false); setError(null); }}>
                      Редагувати
                    </button>{' '}
                    <button onClick={() => removeMut.mutate(t.id)}>Видалити</button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TaxationSchemeForm({
  initial, submitting, onSubmit, onCancel,
}: {
  initial?: PlatformTaxationScheme;
  submitting: boolean;
  onSubmit: (p: PlatformTaxationSchemeCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name_uk ?? '');
  const [hasVat, setHasVat] = useState(initial?.has_vat ?? true);
  const [profit, setProfit] = useState(initial?.profit_tax_rate != null ? String(initial.profit_tax_rate) : '');
  const [single, setSingle] = useState(initial?.single_tax_rate != null ? String(initial.single_tax_rate) : '');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      code: code.trim(), name_uk: name.trim(), has_vat: hasVat,
      profit_tax_rate: profit === '' ? null : Number(profit),
      single_tax_rate: single === '' ? null : Number(single),
      description: initial?.description ?? null,
      is_active: isActive,
    });
  }

  return (
    <form onSubmit={submit} style={{
      display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
      padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: '6px', marginBottom: '0.75rem',
    }}>
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input value={code} maxLength={30} required style={{ width: '160px' }}
          onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '220px' }}>
        <label>Назва*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Прибуток, %</label>
        <input type="number" step="0.01" value={profit} style={{ width: '110px' }}
          onChange={(e) => setProfit(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0 }}>
        <label>Єдиний, %</label>
        <input type="number" step="0.01" value={single} style={{ width: '110px' }}
          onChange={(e) => setSingle(e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={hasVat} style={{ width: 'auto' }}
          onChange={(e) => setHasVat(e.target.checked)} />
        З ПДВ
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={isActive} style={{ width: 'auto' }}
          onChange={(e) => setIsActive(e.target.checked)} />
        Активна
      </label>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>Скасувати</button>
    </form>
  );
}

function TaxOfficesTab() {
  const qc = useQueryClient();
  const KEY = ['platform-tax-offices'];
  const list = useQuery({ queryKey: KEY, queryFn: platformRefs.taxOffices.list });
  const [editing, setEditing] = useState<PlatformTaxOffice | null>(null);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMut = useMutation({
    mutationFn: (p: PlatformTaxOfficeCreate) => platformRefs.taxOffices.create(p),
    onSuccess: () => { setAdding(false); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося створити.')),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: PlatformTaxOfficeCreate }) =>
      platformRefs.taxOffices.update(id, p),
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e) => setError(errMessage(e, 'Не вдалося оновити.')),
  });

  const needle = q.trim().toLowerCase();
  const rows = (list.data ?? []).filter((t) =>
    !needle
    || t.code.toLowerCase().includes(needle)
    || t.name.toLowerCase().includes(needle)
    || (t.region_name ?? '').toLowerCase().includes(needle));

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Глобальний перелік територіальних органів ДПС (код ДПІ → назва).
        Використовується картками організацій (вкладка «Податкові рахунки») і йде у
        DECLARHEAD усіх подань до ДПС. Оновлюйте, коли ДПС змінює структуру.
      </p>
      <div className="toolbar" style={{ marginBottom: '0.75rem', gap: '0.5rem' }}>
        <input placeholder="пошук: код / назва / область" value={q}
          style={{ maxWidth: 280 }} onChange={(e) => setQ(e.target.value)} />
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          {rows.length} з {(list.data ?? []).length}
        </span>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => { setAdding(true); setEditing(null); setError(null); }}>
          + Додати
        </button>
      </div>
      {error && <div className="error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {adding && (
        <TaxOfficeForm
          submitting={createMut.isPending}
          onSubmit={(p) => createMut.mutate(p)}
          onCancel={() => { setAdding(false); setError(null); }}
        />
      )}
      {list.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Код</th>
              <th style={thStyle}>Назва</th>
              <th style={thStyle}>Область</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Активна</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) =>
              editing?.id === t.id ? (
                <tr key={t.id}>
                  <td style={tdStyle} colSpan={5}>
                    <TaxOfficeForm
                      initial={t}
                      submitting={updateMut.isPending}
                      onSubmit={(p) => updateMut.mutate({ id: t.id, p })}
                      onCancel={() => { setEditing(null); setError(null); }}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={t.id}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>{t.code}</td>
                  <td style={tdStyle}>{t.name}</td>
                  <td style={tdStyle}>{t.region_name ?? ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{t.is_active ? '☑' : '☐'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button onClick={() => { setEditing(t); setAdding(false); setError(null); }}>
                      Редагувати
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TaxOfficeForm({
  initial, submitting, onSubmit, onCancel,
}: {
  initial?: PlatformTaxOffice;
  submitting: boolean;
  onSubmit: (p: PlatformTaxOfficeCreate) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [region, setRegion] = useState(initial?.region_name ?? '');
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      code: code.trim(), name: name.trim(),
      region_name: region.trim() || null, is_active: isActive,
    });
  }

  return (
    <form onSubmit={submit} style={{
      display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end',
      padding: '0.75rem', background: 'var(--bg-soft-2)', borderRadius: '6px', marginBottom: '0.75rem',
    }}>
      <div className="field" style={{ margin: 0 }}>
        <label>Код*</label>
        <input value={code} maxLength={10} required style={{ width: '90px' }}
          onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, flex: 1, minWidth: '260px' }}>
        <label>Назва*</label>
        <input value={name} required onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field" style={{ margin: 0, minWidth: '180px' }}>
        <label>Область</label>
        <input value={region} onChange={(e) => setRegion(e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
        <input type="checkbox" checked={isActive} style={{ width: 'auto' }}
          onChange={(e) => setIsActive(e.target.checked)} />
        Активна
      </label>
      <button type="submit" className="primary" disabled={submitting || !code.trim() || !name.trim()}>
        {submitting ? 'Збереження…' : 'Зберегти'}
      </button>
      <button type="button" onClick={onCancel}>Скасувати</button>
    </form>
  );
}


// ═══ Сертифікати ДПС (0453, 2026-09-01) ═══════════════════════════════════
// tax.gov.ua за Akamai гео-блокує сервер (403 на data/files/*.cer) —
// автозамін неможливий. Власник качає .cer у СВОЄМУ браузері (український
// IP) і завантажує тут; сервер класифікує за роллю (CN + keyUsage) і
// валідує поля. Фонову свіжість строків показує воркер (assets-status).
function DpsCertsTab() {
  const qc = useQueryClient();
  const [report, setReport] = useState<
    { applied: number; results: import('@/api/platformRefs').DpsCertUploadResult[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['platform-dps-certs'],
    queryFn: platformEusign.dpsCerts,
  });
  const upload = useMutation({
    mutationFn: platformEusign.uploadDpsCerts,
    onSuccess: (rep) => {
      setReport(rep); setErr(null);
      qc.invalidateQueries({ queryKey: ['platform-dps-certs'] });
    },
    onError: (e) => {
      const d = (e as AxiosError<{ detail?: unknown }>).response?.data?.detail;
      setErr(typeof d === 'string' ? d : String(e));
      setReport(null);
    },
  });
  const onFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = await Promise.all([...list].map(async (f) => {
      const buf = new Uint8Array(await f.arrayBuffer());
      let bin = '';
      buf.forEach((b) => { bin += String.fromCharCode(b); });
      return { name: f.name, cer_base64: btoa(bin) };
    }));
    upload.mutate(files);
  };
  const fmtD = (v: string | null | undefined) =>
    v ? new Date(v).toLocaleDateString('uk-UA') : '—';
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Сертифікати ДПС</h3>
      <p style={{ color: 'var(--muted)', fontSize: '0.88rem', maxWidth: 720 }}>
        tax.gov.ua гео-блокує наш сервер (Akamai, 403) — автоматичне
        завантаження неможливе. При ротації (анонс на «Єдиній адресі»)
        скачайте .cer файли у СВОЄМУ браузері за посиланнями нижче і
        завантажте їх сюди — сервіс сам визначить роль кожного (підпис /
        шифрування, Єдине вікно / API кабінету) і замінить файли.
      </p>
      <TchTable style={{ maxWidth: 860 }}>
        <thead>
          <tr><th style={{ textAlign: 'left' }}>Файл</th>
            <th style={{ textAlign: 'left' }}>Роль</th>
            <th>Діє до</th><th>Днів</th><th>Стан</th></tr>
        </thead>
        <tbody>
          {(q.data?.certs ?? []).map((c) => (
            <tr key={c.name}>
              <td>{c.name}</td>
              <td>{c.role}</td>
              <td style={{ textAlign: 'center' }}>{fmtD(c.not_after)}</td>
              <td style={{ textAlign: 'center' }}>{c.days_left ?? '—'}</td>
              <td style={{ textAlign: 'center' }}>
                {c.expired
                  ? <span style={{ color: '#dc2626', fontWeight: 700 }}>ПРОСТРОЧЕНО</span>
                  : c.expiring
                    ? <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}>спливає</span>
                    : <span style={{ color: 'var(--badge-green-fg)' }}>чинний</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      <div style={{ margin: '0.75rem 0 0.25rem', fontWeight: 600 }}>
        Посилання для скачування (відкриються у вашому браузері):
      </div>
      <ul style={{ margin: '0.25rem 0 0.75rem 1.25rem', fontSize: '0.88rem' }}>
        {(q.data?.download_urls ?? []).map((u) => (
          <li key={u}><a href={u} target="_blank" rel="noreferrer">{u}</a></li>
        ))}
        <li style={{ color: 'var(--muted)' }}>
          при ротації id файлів зміняться — беріть нові посилання з анонсу
          на tax.gov.ua («Єдина адреса», розділ електронної звітності)
        </li>
      </ul>
      <label className="primary" style={{ display: 'inline-block', padding: '0.35rem 0.9rem',
        borderRadius: 6, background: 'var(--primary)', color: '#fff', cursor: 'pointer' }}>
        {upload.isPending ? 'завантаження…' : 'завантажити .cer файли'}
        <input type="file" hidden multiple accept=".cer,.crt,.der"
          onChange={(e) => { void onFiles(e.target.files); e.currentTarget.value = ''; }} />
      </label>
      {err && <div style={{ color: '#dc2626', marginTop: 8, whiteSpace: 'pre-wrap' }}>{err}</div>}
      {report && (
        <div style={{ marginTop: 10, fontSize: '0.88rem' }}>
          <b>Застосовано: {report.applied}</b>
          <ul style={{ margin: '0.25rem 0 0 1.25rem' }}>
            {report.results.map((r, i) => (
              <li key={i} style={{ color: r.applied ? '#15803d' : '#b45309' }}>
                {r.uploaded_as ?? r.file}: {r.applied
                  ? `замінено ${r.file} (${r.role}, діє до ${fmtD(r.not_after)})`
                  : (r.reason ?? 'не застосовано')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
